class StandaloneFloatingVideos extends HTMLElement {
  constructor() {
    super();

    this.widget = this.querySelector(".sfv-widget");
    this.widgetWrap = this.querySelector(".sfv-widget-wrap");
    this.widgetDismiss = this.querySelector(".sfv-widget-dismiss");
    this.widgetVideo = this.querySelector(".sfv-widget-video");
    this.mainDialog = this.querySelector(".sfv-dialog");
    this.dialogCloseBtn = this.querySelector(".sfv-dialog-close");
    this.dialogInner = this.querySelector(".sfv-dialog-inner");
    this.track = this.querySelector(".sfv-carousel-track");
    this.slides = Array.from(this.querySelectorAll(".sfv-carousel-slide"));
    this.prevBtn = this.querySelector('.sfv-carousel-control[name="previous"]');
    this.nextBtn = this.querySelector('.sfv-carousel-control[name="next"]');
    this.viewport = this.querySelector(".sfv-carousel-viewport");

    this.index = 0;
    this.isDragging = false;
    this.dragStart = 0;
    this.scrollStart = 0;
    this.dragDistance = 0;
    this.scrollRaf = null;
    this.suppressScrollSync = false;
    this.pointerSlideIndex = -1;
    this.userPausedActive = false;
    this.showProgress = this.dataset.showProgress !== "false";
    this.closeOnBackdrop = this.dataset.closeOnBackdrop !== "false";
    this.atcMode = (this.dataset.atcMode || "native").toLowerCase();
    this.cartOpenSelector = this.dataset.cartOpenSelector || "";
    this.cartOpenEvent = this.dataset.cartOpenEvent || "";
    this.cartAddUrl = this.dataset.cartAddUrl || "/cart/add.js";
    this.desktopQuery = "(min-width: 750px)";

    this.onDialogNativeClose = this.onDialogNativeClose.bind(this);
    this.onDialogBackdropClick = this.onDialogBackdropClick.bind(this);
    this.onOpenDialog = this.onOpenDialog.bind(this);
    this.onCloseDialog = this.onCloseDialog.bind(this);
    this.onPrev = () => this.go(this.index - 1);
    this.onNext = () => this.go(this.index + 1);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onTrackScroll = this.onTrackScroll.bind(this);
    this.syncWidgetVideo = this.syncWidgetVideo.bind(this);
    this.onCartAdded = this.onCartAdded.bind(this);
    this.onWidgetDismiss = this.onWidgetDismiss.bind(this);
    this.onFormSubmit = this.onFormSubmit.bind(this);
    this.onAtcClick = this.onAtcClick.bind(this);
    this.onResize = this.onResize.bind(this);

    this.init();
  }

  init() {
    this.setupWidgetDismiss();
    this.setupWidgetAutoplay();
    this.setupDialog();

    if (!this.slides.length) return;

    this.prevBtn?.addEventListener("click", this.onPrev);
    this.nextBtn?.addEventListener("click", this.onNext);
    this.viewport?.addEventListener("keydown", this.onKeyDown);

    this.track?.addEventListener("scroll", this.onTrackScroll, { passive: true });
    this.viewport?.addEventListener("pointerdown", this.onPointerDown);
    window.addEventListener("pointermove", this.onPointerMove);
    window.addEventListener("pointerup", this.onPointerUp);
    window.addEventListener("resize", this.onResize);

    this.setupSlideActivation();
    this.setupThemeCartBridge();
    this.setupShoppableVideos();
    this.updateControls();
  }

  setupDialog() {
    if (!this.mainDialog) return;

    this.widget?.addEventListener("click", this.onOpenDialog);
    this.dialogCloseBtn?.addEventListener("click", this.onCloseDialog);
    this.mainDialog.addEventListener("close", this.onDialogNativeClose);
    this.mainDialog.addEventListener("click", this.onDialogBackdropClick);
  }

  onOpenDialog(event) {
    event.preventDefault();
    if (!this.mainDialog || typeof this.mainDialog.showModal !== "function") return;
    if (this.mainDialog.open) return;

    this.mainDialog.showModal();
    this.pauseWidgetVideo();
    this.userPausedActive = false;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.go(this.index, { silent: true });
      });
    });
  }

  onCloseDialog(event) {
    event?.preventDefault();
    this.mainDialog?.close();
  }

  onDialogNativeClose() {
    this.pauseAllSlideVideos();
    this.syncWidgetVideo();
    this.playWidgetVideo();
  }

  onDialogBackdropClick(event) {
    if (!this.closeOnBackdrop || !this.mainDialog?.open) return;
    // Full-bleed dialog: clicks on the shell or padded inner (not the card) close
    if (event.target === this.mainDialog || event.target === this.dialogInner) {
      this.mainDialog.close();
    }
  }

  setupShoppableVideos() {
    this.setupVideoProgressBars();
    this.setupVideoPlayToggle();
  }

  setupVideoProgressBars() {
    if (!this.showProgress) return;

    this.querySelectorAll(".sfv-slide-video").forEach((video) => {
      const wrap = video.closest(".sfv-slide-video-wrap");
      const progressBar = wrap?.querySelector(".sfv-slide-progress-bar");
      if (!progressBar) return;

      video.addEventListener("timeupdate", () => {
        if (!video.duration) return;
        progressBar.style.width = `${(video.currentTime / video.duration) * 100}%`;
      });

      video.addEventListener("ended", () => {
        progressBar.style.width = "0%";
        video.currentTime = 0;
      });
    });
  }

  setupVideoPlayToggle() {
    this.slides.forEach((slide, i) => {
      const playButton = slide.querySelector(".sfv-video-play-button");
      if (!playButton) return;

      playButton.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (i !== this.index) {
          this.userPausedActive = false;
          this.go(i);
          return;
        }

        if (!this.mainDialog?.open) return;

        const video = slide.querySelector(".sfv-slide-video");
        if (!video) return;

        this.toggleActiveSlideVideo(video, slide);
      });
    });
  }

  setSlidePlaying(slide, isPlaying) {
    const wrap = slide?.querySelector(".sfv-slide-video-wrap");
    if (wrap) wrap.dataset.playing = isPlaying ? "true" : "false";
  }

  toggleActiveSlideVideo(video, slide) {
    const wrap = slide.querySelector(".sfv-slide-video-wrap");
    if (!wrap) return;

    if (wrap.dataset.playing === "true" && !video.paused) {
      this.setSlidePlaying(slide, false);
      video.pause();
      this.userPausedActive = true;
      return;
    }

    this.userPausedActive = false;
    this.playSlideVideo(video, slide, { muted: false });
  }

  resetSlideVideo(slide) {
    const video = slide.querySelector(".sfv-slide-video");
    const progressBar = slide.querySelector(".sfv-slide-progress-bar");
    if (!video) return;

    this.setSlidePlaying(slide, false);
    video.pause();
    video.muted = true;
    if (progressBar) progressBar.style.width = "0%";
  }

  setupSlideActivation() {
    this.slides.forEach((slide, i) => {
      const card = slide.querySelector(".sfv-slide-card");
      if (!card) return;

      card.addEventListener("click", (event) => {
        if (this.dragDistance > 8) return;
        if (
          event.target.closest(
            ".sfv-product-actions, .sfv-product-form, .sfv-product-form-host, .sfv-slide-overlay, .sfv-video-play-button, form, button, select, a",
          )
        ) {
          return;
        }
        event.preventDefault();
        this.go(i);
      });

      card.addEventListener("keydown", (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        if (
          event.target.closest(
            ".sfv-product-actions, .sfv-product-form, .sfv-product-form-host, .sfv-slide-overlay, .sfv-video-play-button, form, button, select, a",
          )
        ) {
          return;
        }
        event.preventDefault();
        this.go(i);
      });
    });
  }

  setupThemeCartBridge() {
    this.addEventListener("click", this.onAtcClick, true);
    this.addEventListener("submit", this.onFormSubmit, true);
    window.addEventListener("cart:added", this.onCartAdded);
  }

  onAtcClick(event) {
    if (!event.target.closest(".sfv-atc-btn")) return;
    this.pauseActiveVideo();
  }

  onFormSubmit(event) {
    const form = event.target.closest("form");
    if (!form || !this.contains(form)) return;
    if (!form.closest(".sfv-product-form, .sfv-product-form-host, .sfv-product-actions")) return;

    this.pauseActiveVideo();

    if (this.atcMode !== "ajax") return;

    event.preventDefault();
    event.stopPropagation();
    this.submitCartAjax(form);
  }

  async submitCartAjax(form) {
    if (this._ajaxBusy) return;
    this._ajaxBusy = true;

    const submitBtn = form.querySelector('[type="submit"], .sfv-atc-btn');
    if (submitBtn) submitBtn.disabled = true;

    try {
      const formData = new FormData(form);
      const response = await fetch(this.cartAddUrl, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: formData,
        credentials: "same-origin",
      });

      let detail = null;
      try {
        detail = await response.json();
      } catch (error) {
        detail = { ok: response.ok, status: response.status };
      }

      if (!response.ok) {
        console.warn("[standalone-floating-videos] Cart add failed", detail);
        return;
      }

      this.openThemeCart(detail);
    } catch (error) {
      console.warn("[standalone-floating-videos] Cart add error", error);
    } finally {
      this._ajaxBusy = false;
      if (submitBtn && !submitBtn.hasAttribute("data-sfv-keep-disabled")) {
        submitBtn.disabled = false;
      }
    }
  }

  openThemeCart(detail) {
    const payload = { bubbles: true, detail };

    if (this.cartOpenEvent) {
      window.dispatchEvent(new CustomEvent(this.cartOpenEvent, payload));
    }

    window.dispatchEvent(new CustomEvent("cart:added", payload));

    const selectors = this.cartOpenSelector
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    selectors.forEach((selector) => {
      let el = null;
      try {
        el = document.querySelector(selector);
      } catch (error) {
        return;
      }
      if (!el) return;

      if (typeof el.showModal === "function") {
        try {
          el.showModal();
          return;
        } catch (error) {
          /* continue */
        }
      }

      if (typeof el.show === "function") {
        try {
          el.show();
          return;
        } catch (error) {
          /* continue */
        }
      }

      if ("open" in el) {
        try {
          el.open = true;
          return;
        } catch (error) {
          /* continue */
        }
      }

      if (typeof el.click === "function") {
        el.click();
      }
    });
  }

  onCartAdded() {
    if (!this.mainDialog?.open) return;
    this.pauseActiveVideo();
  }

  pauseActiveVideo() {
    const slide = this.slides[this.index];
    const video = slide?.querySelector(".sfv-slide-video");
    if (!slide || !video) return;
    this.setSlidePlaying(slide, false);
    video.pause();
    this.userPausedActive = true;
  }

  getDismissStorageKey() {
    return `sfv-widget-dismissed-${this.id}`;
  }

  setupWidgetDismiss() {
    if (!this.widgetWrap || !this.widgetDismiss) return;

    try {
      if (sessionStorage.getItem(this.getDismissStorageKey()) === "true") {
        this.widgetWrap.classList.add("is-dismissed");
        return;
      }
    } catch (error) {
      /* ignore storage errors */
    }

    this.widgetDismiss.addEventListener("click", this.onWidgetDismiss);
  }

  onWidgetDismiss(event) {
    event.preventDefault();
    event.stopPropagation();

    this.pauseWidgetVideo();
    this.widgetWrap?.classList.add("is-dismissed");

    try {
      sessionStorage.setItem(this.getDismissStorageKey(), "true");
    } catch (error) {
      /* ignore storage errors */
    }
  }

  setupWidgetAutoplay() {
    if (!this.widgetVideo) return;

    this.widgetVideo.muted = true;
    this.widgetVideo.defaultMuted = true;
    this.widgetVideo.setAttribute("muted", "");
    this.widgetVideo.setAttribute("playsinline", "");

    const tryPlay = () => {
      if (this.mainDialog?.open) return;
      this.playWidgetVideo();
    };

    tryPlay();

    this.widgetVideo.addEventListener("loadeddata", tryPlay, { once: true });
    this.widgetVideo.addEventListener("canplay", tryPlay, { once: true });

    if (!("IntersectionObserver" in window)) return;

    const observeTarget = this.widgetWrap || this.widget || this.widgetVideo;

    this.visibilityObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.mainDialog?.open) {
            tryPlay();
          } else {
            this.pauseWidgetVideo();
          }
        });
      },
      { threshold: 0.1 },
    );

    this.visibilityObserver.observe(observeTarget);
  }

  isDesktop() {
    return window.matchMedia(this.desktopQuery).matches;
  }

  playSlideVideo(video, slide, { muted = false } = {}) {
    if (!video) return;

    this.setSlidePlaying(slide, true);
    video.muted = muted;

    const playPromise = video.play();
    if (playPromise?.catch) {
      playPromise.catch(() => {});
    }
  }

  onResize() {
    this.syncWidgetVideo();
    if (this.mainDialog?.open) {
      this.scrollToSlide(this.slides[this.index], "auto");
    }
  }

  onTrackScroll() {
    if (!this.mainDialog?.open || this.isDragging || this.suppressScrollSync) return;

    if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);

    this.scrollRaf = requestAnimationFrame(() => {
      this.scrollRaf = null;
      const nearest = this.getNearestSlideIndex();
      if (nearest !== this.index) {
        this.setActiveIndex(nearest, { fromScroll: true });
      }
    });
  }

  getNearestSlideIndex() {
    if (!this.track || !this.slides.length) return 0;

    const trackRect = this.track.getBoundingClientRect();
    const scrollCenter = this.isDesktop()
      ? trackRect.left + this.track.clientWidth / 2
      : trackRect.top + this.track.clientHeight / 2;

    let nearest = 0;
    let nearestDist = Infinity;

    this.slides.forEach((slide, i) => {
      const rect = slide.getBoundingClientRect();
      const slideCenter = this.isDesktop()
        ? rect.left + rect.width / 2
        : rect.top + rect.height / 2;
      const dist = Math.abs(slideCenter - scrollCenter);

      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = i;
      }
    });

    return nearest;
  }

  scrollToSlide(slide, behavior = "smooth") {
    if (!this.track || !slide) return;

    this.suppressScrollSync = true;

    if (this.isDesktop()) {
      const trackRect = this.track.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      const slideCenter =
        slideRect.left - trackRect.left + this.track.scrollLeft + slideRect.width / 2;
      const targetScroll = slideCenter - this.track.clientWidth / 2;
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);

      this.track.scrollTo({
        left: Math.min(maxScroll, Math.max(0, targetScroll)),
        behavior,
      });
    } else {
      const trackRect = this.track.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      const slideCenter =
        slideRect.top - trackRect.top + this.track.scrollTop + slideRect.height / 2;
      const targetScroll = slideCenter - this.track.clientHeight / 2;
      const maxScroll = Math.max(0, this.track.scrollHeight - this.track.clientHeight);

      this.track.scrollTo({
        top: Math.min(maxScroll, Math.max(0, targetScroll)),
        behavior,
      });
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.suppressScrollSync = false;
      });
    });
  }

  syncWidgetVideo() {
    if (!this.widgetVideo || !this.slides.length) return;

    const activeSlide = this.slides[this.index];
    const slideVideo = activeSlide?.querySelector(".sfv-slide-video");
    if (!slideVideo) return;

    const source = slideVideo.querySelector("source")?.src || slideVideo.currentSrc || slideVideo.src;
    if (!source) return;

    const widgetSource = this.widgetVideo.querySelector("source");
    if (widgetSource) {
      if (widgetSource.src !== source) widgetSource.src = source;
    } else if (this.widgetVideo.src !== source) {
      this.widgetVideo.src = source;
    }

    if (!this.mainDialog?.open) this.playWidgetVideo();
  }

  playWidgetVideo() {
    if (!this.widgetVideo || this.mainDialog?.open) return;
    if (this.widgetWrap?.classList.contains("is-dismissed")) return;

    this.widgetVideo.muted = true;
    this.widgetVideo.defaultMuted = true;

    const playPromise = this.widgetVideo.play();
    if (playPromise?.catch) {
      playPromise.catch(() => {});
    }
  }

  pauseWidgetVideo() {
    this.widgetVideo?.pause();
  }

  pauseAllSlideVideos() {
    this.slides.forEach((slide) => this.resetSlideVideo(slide));
    this.userPausedActive = false;
  }

  playActiveSlide({ withSound = false, force = false } = {}) {
    this.slides.forEach((slide, i) => {
      const video = slide.querySelector(".sfv-slide-video");
      if (!video) return;

      if (i === this.index) {
        if (this.userPausedActive && !force) {
          this.setSlidePlaying(slide, false);
          video.pause();
          return;
        }

        this.playSlideVideo(video, slide, {
          muted: !withSound,
        });
      } else {
        this.resetSlideVideo(slide);
      }
    });
  }

  setActiveIndex(nextIndex, { fromScroll = false, silent = false } = {}) {
    if (!this.slides.length) return;

    const count = this.slides.length;
    const index = ((nextIndex % count) + count) % count;
    const indexChanged = index !== this.index;

    if (indexChanged) {
      this.userPausedActive = false;
    }

    this.index = index;

    this.slides.forEach((slide, i) => {
      const isActive = i === this.index;
      slide.classList.toggle("is-active", isActive);
      slide.setAttribute("aria-hidden", isActive ? "false" : "true");
    });

    if (!fromScroll) {
      this.scrollToSlide(this.slides[this.index], silent ? "auto" : "smooth");
    }

    this.updateControls();

    if (this.mainDialog?.open) {
      this.playActiveSlide({
        withSound: true,
      });
    } else if (!silent && indexChanged) {
      this.syncWidgetVideo();
    }
  }

  go(nextIndex, options = {}) {
    this.setActiveIndex(nextIndex, options);
  }

  updateControls() {
    const disable = this.slides.length <= 1;
    if (this.prevBtn) this.prevBtn.disabled = disable;
    if (this.nextBtn) this.nextBtn.disabled = disable;
  }

  onKeyDown(event) {
    if (!this.mainDialog?.open) return;
    if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      event.preventDefault();
      this.onPrev();
    }
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      event.preventDefault();
      this.onNext();
    }
  }

  onPointerDown(event) {
    if (!this.mainDialog?.open || !this.track) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;
    if (
      event.target.closest(
        ".sfv-product-actions, .sfv-product-form, .sfv-product-form-host, .sfv-slide-overlay, .sfv-video-play-button, form, button, select, a",
      )
    ) {
      return;
    }

    const slide = event.target.closest(".sfv-carousel-slide");
    this.pointerSlideIndex = slide ? this.slides.indexOf(slide) : -1;

    this.isDragging = true;
    this.dragDistance = 0;
    this.dragStart = this.isDesktop() ? event.clientX : event.clientY;
    this.scrollStart = this.isDesktop() ? this.track.scrollLeft : this.track.scrollTop;
    this.track.setPointerCapture?.(event.pointerId);
    this.track.classList.add("is-dragging");
  }

  onPointerMove(event) {
    if (!this.isDragging || !this.track) return;

    const delta = (this.isDesktop() ? event.clientX : event.clientY) - this.dragStart;
    this.dragDistance = Math.max(this.dragDistance, Math.abs(delta));

    if (this.isDesktop()) {
      this.track.scrollLeft = this.scrollStart - delta;
    } else {
      this.track.scrollTop = this.scrollStart - delta;
    }
  }

  onPointerUp() {
    if (!this.isDragging || !this.track) return;

    const wasTap = this.dragDistance <= 8;
    const tappedSlideIndex = this.pointerSlideIndex;

    this.isDragging = false;
    this.pointerSlideIndex = -1;
    this.track.classList.remove("is-dragging");

    if (wasTap && tappedSlideIndex >= 0) {
      this.setActiveIndex(tappedSlideIndex, { fromScroll: false });
      return;
    }

    const nearest = this.getNearestSlideIndex();
    this.setActiveIndex(nearest, { fromScroll: false });
  }

  disconnectedCallback() {
    if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);

    this.widget?.removeEventListener("click", this.onOpenDialog);
    this.dialogCloseBtn?.removeEventListener("click", this.onCloseDialog);
    this.mainDialog?.removeEventListener("close", this.onDialogNativeClose);
    this.mainDialog?.removeEventListener("click", this.onDialogBackdropClick);

    this.prevBtn?.removeEventListener("click", this.onPrev);
    this.nextBtn?.removeEventListener("click", this.onNext);
    this.viewport?.removeEventListener("keydown", this.onKeyDown);
    this.track?.removeEventListener("scroll", this.onTrackScroll);
    this.viewport?.removeEventListener("pointerdown", this.onPointerDown);
    window.removeEventListener("pointermove", this.onPointerMove);
    window.removeEventListener("pointerup", this.onPointerUp);
    window.removeEventListener("resize", this.onResize);
    this.widgetDismiss?.removeEventListener("click", this.onWidgetDismiss);
    window.removeEventListener("cart:added", this.onCartAdded);
    this.removeEventListener("click", this.onAtcClick, true);
    this.removeEventListener("submit", this.onFormSubmit, true);
    this.visibilityObserver?.disconnect();
    this.pauseWidgetVideo();
    this.pauseAllSlideVideos();
  }
}

if (!customElements.get("standalone-floating-videos")) {
  customElements.define("standalone-floating-videos", StandaloneFloatingVideos);
}
